import { useEffect, useState } from "react";
import { Moon, Sun, ArrowRight, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/components/ThemeProvider";
import { CALENDLY_URL } from "@/lib/config";

const links = [
  { label: "How it Works", href: "#how" },
  { label: "Capabilities", href: "#capabilities" },
  { label: "Vision", href: "#vision" },
];

export default function Nav() {
  const { theme, setTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav border-b border-border" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <img
              src="https://customer-assets.emergentagent.com/job_autonomous-qa-2/artifacts/n0f1n8e2_Asset%201%403x.jpg"
              alt="Liskov AI"
              className="h-8 w-8 rounded-full object-cover ring-1 ring-border group-hover:ring-border-strong transition"
            />
            <span className="font-display font-semibold tracking-tight text-[17px]">Liskov AI</span>
          </a>

          <nav data-testid="nav-links" className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                href={l.href}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border hover:border-border-strong transition-colors text-foreground/80 hover:text-foreground"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <a
              data-testid="nav-cta-demo"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex btn-primary !py-2 !px-4 text-sm"
            >
              Schedule Demo
              <ArrowRight className="h-4 w-4" />
            </a>

            <button
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
              onClick={() => setOpen((o) => !o)}
              className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-full border border-border"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-nav border-t border-border overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary !py-2 !px-4 text-sm w-full justify-center"
                data-testid="nav-mobile-cta"
              >
                Schedule Demo <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function LogoMark() {
  return (
    <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden>
      <rect x="1.5" y="1.5" width="29" height="29" rx="8.5" stroke="hsl(var(--border-strong))" />
      <path d="M9 22V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9 22h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="22.5" cy="10.5" r="2.5" fill="hsl(var(--accent))" />
    </svg>
  );
}
